import React, { useState, useRef, useEffect, useMemo } from "react";
import cn from "classnames";
import clsx from "clsx";
import { Icon } from "react-migration/components/Icon";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";

export type PropertyImage = {
  caption: string;
  filename: string;
  mime_type: string;
  type: "floor_plan" | "property_image" | "image_not_available";
};

interface PropertyImageCarouselProps {
  data?: PropertyImage[];
  withFloorplan?: boolean;
  withThumbnails?: boolean;
  setModalOpen?: () => void;
}

export const PropertyImageCarousel: React.FC<PropertyImageCarouselProps> = ({
  data = [],
  withFloorplan = false,
  withThumbnails = true,
  setModalOpen,
}) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const thumbnailsContainerRef = useRef<HTMLDivElement>(null);
  const thumbnailRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [viewImagesOrFloorplan, setViewImagesOrFloorplan] = useState<"images" | "floorplan">("images");
  const floorplanImages = useMemo(() => {
    return data.filter((image) => image.type === "floor_plan");
  }, [data]);

  const propertyImages = useMemo(() => {
    return data.filter((image) => image.type !== "floor_plan");
  }, [data]);

  const [visibleImages, setVisibleImages] = useState<PropertyImage[]>(propertyImages);

  const showControls = useMemo(() => {
    return viewImagesOrFloorplan === "images"
      ? propertyImages.length > 1 && propertyImages[0].type !== "image_not_available"
      : floorplanImages.length > 1 && floorplanImages[0].type !== "image_not_available";
  }, [viewImagesOrFloorplan, propertyImages, floorplanImages]);

  thumbnailRefs.current = useMemo(() => {
    return thumbnailRefs.current.slice(
      0,
      viewImagesOrFloorplan === "images" ? propertyImages.length : floorplanImages.length
    );
  }, [viewImagesOrFloorplan, propertyImages, floorplanImages]);

  useEffect(() => {
    if (withThumbnails && thumbnailRefs.current[currentIndex]) {
      thumbnailRefs.current[currentIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [currentIndex, withThumbnails]);

  useEffect(() => {
    setVisibleImages(viewImagesOrFloorplan === "images" ? propertyImages : floorplanImages);
  }, [viewImagesOrFloorplan, propertyImages, floorplanImages]);

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? visibleImages.length - 1 : prevIndex - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex === visibleImages.length - 1 ? 0 : prevIndex + 1));
  };

  const goToImage = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <div className="relative w-full h-full max-w-4xl mx-auto overflow-hidden bg-gray-100 rounded-lg shadow-lg group">
      <div className="relative flex items-center justify-center w-full aspect-video">
        <img
          src={visibleImages[currentIndex].filename}
          alt={`Image ${currentIndex + 1}`}
          className={cn("object-cover object-center", {
            "h-full w-auto": viewImagesOrFloorplan === "floorplan",
            "w-full h-full": viewImagesOrFloorplan === "images",
          })}
        />

        {showControls && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute p-2 transition-all duration-200 -translate-y-1/2 rounded-md opacity-0 cursor-pointer left-4 top-1/2 bg-platform-blue-50 hover:bg-platform-blue-50/75 text-platform-blue-950 group-hover:opacity-100"
              aria-label="Previous image"
            >
              <Icon icon="icon-lt-arrow-left-s-line" size="lg" extraClasses="cursor-pointer" />
            </button>

            <button
              onClick={goToNext}
              className="absolute p-2 transition-all duration-200 -translate-y-1/2 rounded-md opacity-0 cursor-pointer right-4 top-1/2 bg-platform-blue-50 hover:bg-platform-blue-50/75 text-platform-blue-950 group-hover:opacity-100"
              aria-label="Next image"
            >
              <Icon icon="icon-lt-arrow-right-s-line" size="lg" extraClasses="cursor-pointer" />
            </button>
          </>
        )}
        <div className="absolute flex flex-row items-center gap-2 bottom-4 right-4">
          {withFloorplan && floorplanImages.length > 0 && propertyImages.length > 0 && (
            <button
              onClick={() => {
                setViewImagesOrFloorplan(viewImagesOrFloorplan === "images" ? "floorplan" : "images");
                setCurrentIndex(0);
              }}
              className={clsx(
                "px-4 py-2 rounded-md font-medium transition-all duration-200 flex items-center gap-2",
                viewImagesOrFloorplan === "floorplan"
                  ? "bg-platform-blue-50 text-platform-blue-950 shadow-lg"
                  : "bg-white text-platform-blue-950 hover:bg-platform-blue-50 shadow-md hover:shadow-lg"
              )}
              aria-label="View floorplan"
            >
              <Icon icon="icon-lt-map-pin-line" size="lg" />
              {viewImagesOrFloorplan === "images"
                ? t("comparables.selection_details.images.floorplan", {
                    count: floorplanImages.length,
                    plural: floorplanImages.length > 1 ? "s" : "",
                  })
                : t("comparables.selection_details.images.images", {
                    count: propertyImages.length,
                    plural: propertyImages.length > 1 ? "s" : "",
                  })}
            </button>
          )}
          {setModalOpen && (
            <button
              onClick={() => setModalOpen()}
              className="p-1.5 rounded-md font-medium transition-all duration-200 flex items-center gap-2 bg-white text-platform-blue-950 hover:bg-platform-blue-50 shadow-md hover:shadow-lg"
              aria-label="View full size"
            >
              <Icon icon="icon-lt-fullscreen-line" size="lg" />
            </button>
          )}
        </div>
        {visibleImages[currentIndex].caption && (
          <div
            className={cn(
              "absolute bottom-4 left-4 px-4 py-2 max-w-[55%]",
              "bg-platform-blue-50/50 text-platform-blue-950",
              "rounded-md"
            )}
          >
            <div className="relative p-0 m-0 group/tooltip">
              <p className="m-0! text-sm truncate cursor-default">{visibleImages[currentIndex].caption}</p>
              <span
                className={cn(
                  "hidden group-hover/tooltip:block",
                  "absolute w-max z-10 -top-8 left-0",
                  "bg-white text-platform-blue-950 text-xs px-2 py-1",
                  "rounded-sm shadow-lg border border-text-base-primary",
                  "transition duration-200 delay-75"
                )}
              >
                {visibleImages[currentIndex].caption}
              </span>
            </div>
          </div>
        )}
      </div>

      {withThumbnails && showControls && (
        <div ref={thumbnailsContainerRef} className="flex w-full gap-2 p-4 overflow-x-scroll bg-white">
          {visibleImages.map((image, index) => (
            <button
              key={index}
              ref={(el) => (thumbnailRefs.current[index] = el)}
              onClick={() => goToImage(index)}
              className={`shrink-0 w-16 h-12 rounded-md overflow-hidden !border border-solid! transition-all duration-200 ${
                index === currentIndex
                  ? "border-2! border-platform-blue-500 shadow-md"
                  : "border-blue-grey-200 hover:border-platform-blue-500"
              }`}
            >
              <img src={image.filename} alt={`Thumbnail ${index + 1}`} className="object-cover w-full h-full" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
